// Copyright (c) Facebook, Inc. and its affiliates.
import React from 'react';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Countdown from 'react-countdown-now';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { withStyles } from '@material-ui/core';
import LeaderBoard from './LeaderBoard';

import People from './People';
import * as challengePeople from '../challenge_people.json';

import moment from 'moment-timezone';

const Completionist = () => <span>Challenge has completed!</span>

const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return <Completionist />;
    } else {
        // Render a countdown
        return <span>{days} days {hours}h {minutes}m {seconds}s</span>;
    }
};

const styles = (theme) => ({
    title: {
        marginTop: '5em'
    },
    overview: {
        marginTop: '2em'
    },
    headings: {
        marginTop: '1em'
    },
    ulItems: {
        paddingLeft: '1.2em'
    },
    preParent: {
        backgroundColor: '#eee',
        border: '1px solid #ddd',
        borderRadius: '2px',
        '& pre': {
            whiteSpace: 'pre-wrap',
        },
        '& span': {
            padding: '1em',
        },
        '& code': {
            fontSize: '12px',
            color: '#000'
        }
    },
    evalAILink: {
        wordBreak: 'break-word'
    },
    teaserImage: {
        width: '100%',
    },
    spanTypography: {
        display: 'inline-block'
    },
    bannerLogo: {
        width: '100%'
    },
    sectionHeader: {
        marginTop: '0.35em'
    },
    evalaiLogo: {
        marginLeft: '-3px'
    }
});

const Challenge2020 = (props) => {
    const deadline = moment.tz("2019-05-27T23:59:59", "Etc/GMT").toDate();
    return (
        <Grid
            container
            justify="center"
            alignItems="center"
        >
            <Grid
                item
                xs={10}
                md={8}
                lg={7}
            >
                <Grid
                    container
                    justify="flex-start"
                    alignItems="flex-start"
                    spacing={16}
                >
                    <Grid item className={props.classes.title} xs={12}>
                        <Typography variant="h3" align="left">
                            LVIS Challenge 2020
                        </Typography>
                    </Grid>


                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h4" align="left">
                            Overview
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        Today, rigorous evaluation of general purpose object detectors is mostly performed in the limited category regime (e.g.
                        80) or when there are a large number of training examples
                        per category (e.g. 100 to 1000+). LVIS provides an opportunity to enable research in the setting where there are a large
                        number of categories and where per-category data is sometimes scarce.
                        </Typography>
                        <Typography variant="subtitle1" className={props.classes.headings} align="left">
                            Given that state-of-the-art deep learning methods for object detection perform poorly
                            in the low-sample regime, we believe that our dataset poses an important and exciting
                            new scientific challenge.
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Important Updates
                        </Typography>
                        
                        <Typography variant="subtitle1" className={props.classes.headings} align="left">
                            <ul>
                                <li><b>Update July 24, 2020</b> Deadline extension: the Challenge deadline is now 07 August 2020 at 23:59:59 PDT.</li>
                                <li><b>Update July 22, 2020</b> We added the following Rules clarification: <em>The LVIS validation set is consider a source of "external annotations". Training on the union of the train set and the validation set is not permitted.</em></li>
                            </ul>
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Rules and Awards
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        Please review the rules as they are different compared to the 2019 challenge.
                        <ol>
                            <li> <b>[NEW] Model ensembles are not permitted.</b> It is difficult to define precisely what constitutes an ensemble, 
                            so use your best judgement. A clear example of an ensemble is training multiple independent models and 
                            combining their results. If unsure, it's likely not permitted, but at the very least carefully
                             describe what you did in your tech report.</li>
                            <li> <b>[NEW] Use of external annotations is not permitted with one exception: 
                                the standard 1000-category ImageNet training set with its classification 
                                labels is allowed.</b>
                                <ul>
                                    <li>The LVIS validation set is consider a source of "external annotations". Training on the union of the train set and the validation set is <b>not permitted</b>.</li>
                                    <li>Using other annotation data, such as COCO annotations, OpenImages annotations,
                                        ImageNet localization/detection annotations, etc. is <b>not</b> permitted.</li>
                                    <li><b>Using additional images and videos without annotations is permitted.</b>
                                        The spirit of this rule is to enable the use of unsupervised and semi-superivsed learning techniques.
                                    </li>
                                </ul>
                                </li> 
                            <li>Participants must submit a technical report that includes a detailed ablation study 
                                of their submission (suggested length 1-4 pages). The reports will be made public. 
                                Only submissions with the report will be considered for any award and will be put 
                                in the LVIS leaderboard. We will provide submission instructions in the near future.</li>
                            <li>For each challenge track we will have two different awards: best result award and 
                                most innovative award. The most innovative award will be based on the method description 
                                in the submitted technical reports and decided by the COCO award committee. The committee 
                                will invite teams to present at the workshop based on the innovations of the submissions 
                                rather than the best scores.</li>
                            <li>There will be a single best paper award for the most innovative and successful solution across 
                                all workshop challenges. The winner will be determined by the workshop organization committee.</li>
                            <li>While not a binding rule, you should also see <Link href="https://www.lvisdataset.org/bestpractices">best practices</Link> for working with LVIS.</li>
                        </ol>
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Evaluation Server
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The Challenge is hosted on <Link href="https://evalai.cloudcv.org/">EvalAI</Link>.
                        The Challenge evaluation server is now live at <Link href="https://evalai.cloudcv.org/web/challenges/challenge-page/669/overview">LVIS Challenge 2020</Link>.
                        Results on the test-dev set (for tech reports) can be computed by submitting results to the <Link href="https://evalai.cloudcv.org/web/challenges/challenge-page/675/overview">LVIS v1.0 Evaluation Server</Link>.
                        </Typography>
                    </Grid>

                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Baselines
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        The following are mask AP baselines on the three LVIS V1 evaluation sets. The baseline model configuration is:
                        <ul>
                            <li>Mask R-CNN with a ResNeXt-101-32x8 backbone</li>
                            <li>Two-phase training similar to the "classifier retraining (cRT)" method from <em><Link href="https://arxiv.org/abs/1910.09217">Decoupling Representation and Classifier for Long-Tailed Recognition.</Link> Kang et al. ICLR 2020.</em></li>
                            <li>Reported results are the mean over 5 runs &plusmn; one standard deviation</li>
                            <li>This method outperforms last year's "repeat factor sampling" baseline by 1.1% AP</li>
                            <li>Additional details will be made available in an updated tech report</li>
                        </ul>
                        </Typography>

                        <Table striped bordered hover size="sm">
                            <thead>
                                <tr>
                                <th>V1 evaluation set</th>
                                <th>AP</th>
                                <th>AP<sub>r</sub></th>
                                <th>AP<sub>c</sub></th>
                                <th>AP<sub>f</sub></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                <td>val</td>
                                <td>27.2 &plusmn;0.17</td>
                                <td>19.6 &plusmn;0.50</td>
                                <td>26.0 &plusmn;0.33</td>
                                <td>31.9 &plusmn;0.06</td>
                                </tr>
                                <tr>
                                <td>test-dev</td>
                                <td>26.8 &plusmn;0.11</td>
                                <td>19.0 &plusmn;0.93</td>
                                <td>25.2 &plusmn;0.21</td>
                                <td>32.0 &plusmn;0.07</td>
                                </tr>
                                <tr>
                                <td>test-challenge</td>
                                <td>26.7 &plusmn;0.17</td>
                                <td>19.0 &plusmn;0.95</td>
                                <td>25.2 &plusmn;0.23</td>
                                <td>32.1 &plusmn;0.12</td>
                                </tr>
                            </tbody>
                        </Table>
                    </Grid>


                    <Grid item className={props.classes.overview} xs={12}>
                        <Typography variant="h5" align="left">
                        Challenge Results
                        </Typography>

                        <Typography variant="subtitle1" className={props.classes.headings}  align="left">
                        Leaderboard for the second <Link href="https://evalai.cloudcv.org/web/challenges/challenge-page/669/overview"> LVIS Challenge </Link> 
                        at ECCV 2020 is now public. The awards committee selected team lvisTraveler as winners based on their overall 
                        AP. In addition, team Asynchronous SSL was awarded the “most innovative” entry award.
                        </Typography>

                        <LeaderBoard year={2020}/>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>

      )
}

export default withStyles(styles)(Challenge2020);
